import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { Box, TextField, Button, Paper, Typography, Alert, IconButton, Menu, MenuItem, CircularProgress, InputAdornment, Link, Dialog, DialogTitle, DialogContent, Card, CardContent, CardActionArea, DialogActions } from '@mui/material';
import { Lock as LockIcon, Person as PersonIcon, Description as DescriptionIcon, Email as EmailIcon, Phone as PhoneIcon, AccountBalance as AccountBalanceIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import config from '../../config';
import { loginRequest, logout, clearError, selectLoginLoading, selectLoginError, selectIsAuthenticated, selectRequires2FA, cancelTwoFactor, selectRequiresPasswordChange } from './slice';
import { getFirstAccessibleRoute } from '../../utils/permissions';
import TwoFactorVerification from './TwoFactorVerification';
import ChangePasswordDialog from './ChangePasswordDialog';
import vneidAccountService from '../../services/vneidAccountService';

function Login() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [ssoLoading, setSsoLoading] = useState(false);
    const [ssoError, setSsoError] = useState(null);
    const [ssoDialogOpen, setSsoDialogOpen] = useState(false);
    const [ssoOptionLoading, setSsoOptionLoading] = useState(null); // Track which option is loading
    const [vneidLoginDialogOpen, setVneidLoginDialogOpen] = useState(false);
    const [selectedOptionType, setSelectedOptionType] = useState(null);
    const [vneidPersonalId, setVneidPersonalId] = useState('');
    const [vneidPassword, setVneidPassword] = useState('');
    const [showVneidPassword, setShowVneidPassword] = useState(false);
    const [vneidLoginLoading, setVneidLoginLoading] = useState(false);
    const [vneidLoginError, setVneidLoginError] = useState(null);

    const loading = useSelector(selectLoginLoading);
    const error = useSelector(selectLoginError);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const requires2FA = useSelector(selectRequires2FA);
    const requiresPasswordChange = useSelector(selectRequiresPasswordChange);

    // Track if session has been cleared - use ref to avoid re-renders
    const sessionCleared = useRef(false);

    // Clear all cookies and reset auth state when login page loads (only once)
    useEffect(() => {
        if (!sessionCleared.current) {
            sessionCleared.current = true;
            Cookies.remove('token', { path: '/' });
            Cookies.remove('refreshToken', { path: '/' });
            Cookies.remove('user', { path: '/' });
            Cookies.remove('permissions', { path: '/' });
            dispatch(logout());
            dispatch(cancelTwoFactor());
        }
    }, [dispatch]);

    // Redirect to first accessible page if authenticated
    // Only redirect if session was already cleared (meaning user just logged in successfully)
    useEffect(() => {
        if (sessionCleared.current && isAuthenticated) {
            const firstAccessibleRoute = getFirstAccessibleRoute();
            navigate(firstAccessibleRoute, { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!username || !password) {
            return;
        }

        dispatch(loginRequest({ username, password }));
    };

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
        if (error) {
            dispatch(clearError());
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (error) {
            dispatch(clearError());
        }
    };

    const handleLanguageMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setAnchorEl(null);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        handleLanguageMenuClose();
    };

    const handleSSOLogin = () => {
        // Open dialog to show login options
        setSsoDialogOpen(true);
        setSsoError(null);
    };

    const handleSSOOptionClick = (optionType) => {
        // Store selected option type and open VNeID login dialog
        setSelectedOptionType(optionType);
        setSsoDialogOpen(false);
        setVneidLoginDialogOpen(true);
        setVneidLoginError(null);
        setVneidPersonalId('');
        setVneidPassword('');
    };

    const handleSSODialogClose = () => {
        if (!ssoOptionLoading) {
            setSsoDialogOpen(false);
            setSsoError(null);
        }
    };

    const handleVneidLoginDialogClose = () => {
        if (!vneidLoginLoading) {
            setVneidLoginDialogOpen(false);
            setVneidLoginError(null);
            setVneidPersonalId('');
            setVneidPassword('');
            setSelectedOptionType(null);
        }
    };

    const handleVneidLoginSubmit = async (e) => {
        e.preventDefault();

        if (!vneidPersonalId || !vneidPassword) {
            setVneidLoginError(t('login.vneidLogin.allFieldsRequired'));
            return;
        }

        setVneidLoginLoading(true);
        setVneidLoginError(null);

        try {
            // Get client ID and redirect URI from config
            const clientId = config.vneid.clientId || '';
            const redirectUri = config.vneid.redirectURL || `${window.location.origin}/auth/callback`;

            if (!clientId) {
                setVneidLoginError(t('login.ssoError'));
                setVneidLoginLoading(false);
                return;
            }

            // Generate state for CSRF protection
            const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

            // Call API to get auth URL
            const response = await vneidAccountService.getAuthUrl({
                clientId: clientId,
                redirectUri: redirectUri,
                responseType: 'code',
                scope: 'openid',
                platform: 'web',
                state: state,
            });

            if (response && response.authUrl) {
                // Store state, option type, and credentials in sessionStorage
                sessionStorage.setItem('sso_state', state);
                sessionStorage.setItem('sso_option_type', selectedOptionType);
                sessionStorage.setItem('vneid_personal_id', vneidPersonalId);
                sessionStorage.setItem('vneid_password', vneidPassword);
                
                // Close dialog
                setVneidLoginDialogOpen(false);
                setVneidLoginLoading(false);
                
                // Redirect to auth URL
                window.location.href = response.authUrl;
            } else {
                setVneidLoginError(t('login.ssoError'));
                setVneidLoginLoading(false);
            }
        } catch (error) {
            console.error('VNeID login error:', error);
            setVneidLoginError(t('login.vneidLogin.loginFailed'));
            setVneidLoginLoading(false);
        }
    };

    const getCurrentLanguageFlag = () => {
        return i18n.language === 'vi' ? '🇻🇳' : '🇺🇸';
    };

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100vh',
                width: '100%',
                overflow: 'hidden',
                margin: 0,
                padding: 0,
                backgroundImage: 'url(/background.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'relative',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {/* Language Switcher - Top Right */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 20,
                    right: 20,
                    zIndex: 1000,
                }}
            >
                <IconButton
                    onClick={handleLanguageMenuOpen}
                    id="languageSwitcher"
                    name="languageSwitcher"
                    data-testid="login-button-languageSwitcher"
                    sx={{
                        width: 50,
                        height: 50,
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        backgroundColor: 'transparent',
                        color: 'white',
                        fontSize: '24px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                    }}
                >
                    {getCurrentLanguageFlag()}
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleLanguageMenuClose}>
                    <MenuItem
                        onClick={() => changeLanguage('en')}
                        selected={i18n.language === 'en'}
                        id="languageEn"
                        name="languageEn"
                        data-testid="login-menuitem-languageEn"
                    >
                        <span style={{ marginRight: 12, fontSize: 18 }}>🇺🇸</span>
                        <span>English</span>
                    </MenuItem>
                    <MenuItem
                        onClick={() => changeLanguage('vi')}
                        selected={i18n.language === 'vi'}
                        id="languageVi"
                        name="languageVi"
                        data-testid="login-menuitem-languageVi"
                    >
                        <span style={{ marginRight: 12, fontSize: 18 }}>🇻🇳</span>
                        <span>Tiếng Việt</span>
                    </MenuItem>
                </Menu>
            </Box>

            {/* Center - Login Form Modal */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 50px',
                    zIndex: 10,
                    position: 'relative',
                    backgroundColor: 'rgba(0, 0, 0, 0.35)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    minWidth: '420px',
                    maxWidth: '500px',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                    }}
                >
                        {/* Logo/Icon Section - Only show when not in 2FA mode */}
                        {!requires2FA && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '60px',
                                    gap: '20px',
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/logo.png"
                                    alt="Logo"
                                    sx={{
                                        height: '40px',
                                        width: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontSize: '27px',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1.5px',
                                        lineHeight: '40px',
                                    }}
                                >
                                    {t('login.systemTitle')}
                                </Typography>
                            </Box>
                        )}

                        {/* Password Change Dialog, 2FA Verification or Login Form */}
                        {requiresPasswordChange ? (
                            <ChangePasswordDialog />
                        ) : requires2FA ? (
                            <TwoFactorVerification />
                        ) : (
                        /* Login Form */
                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <TextField
                                type="text"
                                value={username}
                                onChange={handleUsernameChange}
                                placeholder={t('login.usernamePlaceholder')}
                                disabled={loading}
                                autoComplete="username"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    id: 'username',
                                    name: 'username',
                                    'data-testid': 'login-input-username',
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                        color: 'white',
                                        fontSize: '16px',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '14px',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />

                            <TextField
                                type="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder={t('login.passwordPlaceholder')}
                                disabled={loading}
                                autoComplete="current-password"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    id: 'password',
                                    name: 'password',
                                    'data-testid': 'login-input-password',
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                        color: 'white',
                                        fontSize: '16px',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '14px',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />

                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        fontSize: '14px',
                                        backgroundColor: 'rgba(255, 0, 0, 0.15)',
                                        color: 'white',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        '& .MuiAlert-icon': {
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {(() => {
                                        if (typeof error !== 'string') {
                                            return t('login.error');
                                        }

                                        const normalized = error.trim();
                                        if (!normalized) {
                                            return t('login.error');
                                        }

                                        const normalizedLower = normalized.toLowerCase();
                                        if (normalizedLower === 'account is locked. please contact the administrator.') {
                                            return t('login.errors.accountLocked');
                                        }
                                        if (normalizedLower === 'account is inactive. please contact the administrator.') {
                                            return t('login.errors.accountInactive');
                                        }
                                        if (normalizedLower === 'password must be at least 6 characters') {
                                            return t('login.errors.passwordMinLength');
                                        }

                                        return normalized;
                                    })()}
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                <Button
                                    type="submit"
                                    disabled={loading || !username || !password}
                                    id="submit"
                                    name="submit"
                                    data-testid="login-button-submit"
                                    variant="outlined"
                                    sx={{
                                        padding: '10px 32px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: 'white',
                                        borderColor: 'white',
                                        borderWidth: '1px',
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            borderColor: 'white',
                                        },
                                        '&:disabled': {
                                            borderColor: 'rgba(255, 255, 255, 0.3)',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                        },
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <CircularProgress size={20} sx={{ color: 'white', marginRight: 1 }} />
                                            {t('login.loggingIn')}
                                        </>
                                    ) : (
                                        t('login.loginButton')
                                    )}
                                </Button>

                                {/* Divider */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        margin: '8px 0',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: '1px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '12px',
                                        }}
                                    >
                                        {t('login.or')}
                                    </Typography>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: '1px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        }}
                                    />
                                </Box>

                                {/* SSO Login Button */}
                                <Button
                                    onClick={handleSSOLogin}
                                    disabled={ssoLoading || loading}
                                    id="ssoLogin"
                                    name="ssoLogin"
                                    data-testid="login-button-sso"
                                    variant="outlined"
                                    startIcon={ssoLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AccountBalanceIcon />}
                                    sx={{
                                        padding: '10px 32px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: 'white',
                                        borderColor: 'white',
                                        borderWidth: '1px',
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            borderColor: 'white',
                                        },
                                        '&:disabled': {
                                            borderColor: 'rgba(255, 255, 255, 0.3)',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                        },
                                    }}
                                >
                                    {ssoLoading ? t('login.ssoLoading') : t('login.ssoButton')}
                                </Button>

                                {ssoError && (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            fontSize: '14px',
                                            backgroundColor: 'rgba(255, 0, 0, 0.15)',
                                            color: 'white',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            '& .MuiAlert-icon': {
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        {ssoError}
                                    </Alert>
                                )}
                            </Box>
                        </Box>
                        )}
                    </Box>

                {/* SSO Login Dialog */}
                <Dialog
                    open={ssoDialogOpen}
                    onClose={handleSSODialogClose}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            padding: '24px',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            paddingBottom: '8px',
                        }}
                    >
                        {t('login.ssoDialog.title')}
                    </DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                color: 'text.secondary',
                                marginBottom: '16px',
                            }}
                        >
                            {t('login.ssoDialog.subtitle')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                color: 'primary.main',
                                fontWeight: 600,
                                marginBottom: '24px',
                            }}
                        >
                            {t('login.ssoDialog.category')}
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                gap: '16px',
                                marginBottom: '16px',
                            }}
                        >
                            {/* Option 1: National Public Service Portal */}
                            <Card
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        boxShadow: 4,
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                <CardActionArea
                                    onClick={() => handleSSOOptionClick('national_portal')}
                                    disabled={ssoOptionLoading !== null}
                                    sx={{
                                        padding: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minHeight: '200px',
                                    }}
                                >
                                    {ssoOptionLoading === 'national_portal' ? (
                                        <CircularProgress size={40} sx={{ marginBottom: 2 }} />
                                    ) : (
                                        <>
                                            <Box
                                                component="img"
                                                src="/quoc_huy.svg"
                                                alt="Quốc huy"
                                                sx={{
                                                    width: '80px',
                                                    height: '80px',
                                                    objectFit: 'contain',
                                                    marginBottom: '16px',
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textAlign: 'center',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {t('login.ssoDialog.option1.title')}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textAlign: 'center',
                                                    color: 'text.secondary',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                {t('login.ssoDialog.option1.description')}
                                            </Typography>
                                        </>
                                    )}
                                </CardActionArea>
                            </Card>

                            {/* Option 2: VNeID for Citizens */}
                            <Card
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        boxShadow: 4,
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                <CardActionArea
                                    onClick={() => handleSSOOptionClick('vneid_citizen')}
                                    disabled={ssoOptionLoading !== null}
                                    sx={{
                                        padding: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minHeight: '200px',
                                    }}
                                >
                                    {ssoOptionLoading === 'vneid_citizen' ? (
                                        <CircularProgress size={40} sx={{ marginBottom: 2 }} />
                                    ) : (
                                        <>
                                            <Box
                                                component="img"
                                                src="/logo_bca.png"
                                                alt="VNeID Logo"
                                                sx={{
                                                    width: '80px',
                                                    height: '80px',
                                                    objectFit: 'contain',
                                                    marginBottom: '16px',
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textAlign: 'center',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {t('login.ssoDialog.option2.title')}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textAlign: 'center',
                                                    color: 'text.secondary',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                {t('login.ssoDialog.option2.description')}
                                            </Typography>
                                        </>
                                    )}
                                </CardActionArea>
                            </Card>

                            {/* Option 3: VNeID for Organizations */}
                            <Card
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        boxShadow: 4,
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                <CardActionArea
                                    onClick={() => handleSSOOptionClick('vneid_organization')}
                                    disabled={ssoOptionLoading !== null}
                                    sx={{
                                        padding: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minHeight: '200px',
                                    }}
                                >
                                    {ssoOptionLoading === 'vneid_organization' ? (
                                        <CircularProgress size={40} sx={{ marginBottom: 2 }} />
                                    ) : (
                                        <>
                                            <Box
                                                component="img"
                                                src="/logo_bca.png"
                                                alt="VNeID Logo"
                                                sx={{
                                                    width: '80px',
                                                    height: '80px',
                                                    objectFit: 'contain',
                                                    marginBottom: '16px',
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textAlign: 'center',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {t('login.ssoDialog.option3.title')}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textAlign: 'center',
                                                    color: 'text.secondary',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                {t('login.ssoDialog.option3.description')}
                                            </Typography>
                                        </>
                                    )}
                                </CardActionArea>
                            </Card>
                        </Box>

                        {/* System Notification */}
                        <Alert
                            severity="info"
                            sx={{
                                marginTop: '16px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                '& .MuiAlert-icon': {
                                    color: '#856404',
                                },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: '4px',
                                }}
                            >
                                {t('login.ssoDialog.notification.title')}
                            </Typography>
                            <Typography variant="body2">
                                {t('login.ssoDialog.notification.message')}
                            </Typography>
                        </Alert>

                        {ssoError && (
                            <Alert
                                severity="error"
                                sx={{
                                    marginTop: '16px',
                                }}
                            >
                                {ssoError}
                            </Alert>
                        )}
                    </DialogContent>
                </Dialog>

                {/* VNeID Login Dialog */}
                <Dialog
                    open={vneidLoginDialogOpen}
                    onClose={handleVneidLoginDialogClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            padding: '24px',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            paddingBottom: '8px',
                        }}
                    >
                        {t('login.vneidLogin.title')}
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            component="form"
                            onSubmit={handleVneidLoginSubmit}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                marginTop: '8px',
                            }}
                        >
                            {/* Personal Identification Number Input */}
                            <TextField
                                type="text"
                                value={vneidPersonalId}
                                onChange={(e) => setVneidPersonalId(e.target.value)}
                                placeholder={t('login.vneidLogin.personalIdPlaceholder')}
                                disabled={vneidLoginLoading}
                                autoComplete="username"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#bdbdbd',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#d32f2f',
                                        },
                                    },
                                }}
                            />

                            {/* Password Input */}
                            <TextField
                                type={showVneidPassword ? 'text' : 'password'}
                                value={vneidPassword}
                                onChange={(e) => setVneidPassword(e.target.value)}
                                placeholder={t('login.vneidLogin.passwordPlaceholder')}
                                disabled={vneidLoginLoading}
                                autoComplete="current-password"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowVneidPassword(!showVneidPassword)}
                                                edge="end"
                                                disabled={vneidLoginLoading}
                                            >
                                                {showVneidPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#bdbdbd',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#d32f2f',
                                        },
                                    },
                                }}
                            />

                            {vneidLoginError && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        fontSize: '14px',
                                    }}
                                >
                                    {vneidLoginError}
                                </Alert>
                            )}

                            {/* Login Button */}
                            <Button
                                type="submit"
                                disabled={vneidLoginLoading || !vneidPersonalId || !vneidPassword}
                                variant="contained"
                                fullWidth
                                sx={{
                                    backgroundColor: '#d32f2f',
                                    color: 'white',
                                    padding: '12px',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#b71c1c',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        color: 'rgba(0, 0, 0, 0.26)',
                                    },
                                }}
                            >
                                {vneidLoginLoading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ color: 'white', marginRight: 1 }} />
                                        {t('login.vneidLogin.loggingIn')}
                                    </>
                                ) : (
                                    t('login.vneidLogin.loginButton')
                                )}
                            </Button>

                        </Box>
                    </DialogContent>
                </Dialog>
            </Box>

            {/* Footer - Bottom of screen */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '16px 0',
                    zIndex: 100,
                }}
            >
                <Box
                    sx={{
                        width: '500px',
                        maxWidth: '95%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    }}
                >
                        {/* User Guide Link */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                margin: 0,
                                padding: 0,
                            }}
                        >
                            <DescriptionIcon sx={{ color: '#a8d5ba', fontSize: '16px', flexShrink: 0 }} />
                            <Link
                                href={config.app.tutorialUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                id="tutorialLink"
                                name="tutorialLink"
                                data-testid="login-link-tutorial"
                                sx={{
                                    color: '#a8d5ba',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    textDecoration: 'underline',
                                    lineHeight: '1.5',
                                    '&:hover': {
                                        color: 'white',
                                    },
                                }}
                            >
                                {t('login.tutorial')}
                            </Link>
                        </Box>

                        {/* Contact Information */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                flexWrap: 'wrap',
                                margin: 0,
                                padding: 0,
                            }}
                        >
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    color: '#a8d5ba',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    lineHeight: '1.5',
                                }}
                            >
                                {t('login.contactSupport')}:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <EmailIcon sx={{ color: '#a8d5ba', fontSize: '14px', flexShrink: 0 }} />
                                <Link
                                    href={`mailto:${config.app.supportEmail}`}
                                    underline="none"
                                    sx={{
                                        color: '#a8d5ba',
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                        '&:hover': {
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {config.app.supportEmail}
                                </Link>
                            </Box>
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    color: '#a8d5ba',
                                    fontSize: '14px',
                                    margin: '0 6px',
                                    lineHeight: '1.5',
                                }}
                            >
                                |
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <PhoneIcon sx={{ color: '#a8d5ba', fontSize: '14px', flexShrink: 0 }} />
                                <Link
                                    href={`tel:${config.app.supportPhone.replace(/\s/g, '')}`}
                                    underline="none"
                                    sx={{
                                        color: '#a8d5ba',
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                        '&:hover': {
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {config.app.supportPhone}
                                </Link>
                            </Box>
                        </Box>

                        {/* Divider */}
                        <Box
                            sx={{
                                height: '1px',
                                backgroundColor: 'rgba(168, 213, 186, 0.3)',
                                width: '100%',
                                margin: '4px 0',
                            }}
                        />

                        {/* Copyright and Version */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#a8d5ba',
                                fontSize: '14px',
                                fontWeight: 400,
                                textAlign: 'left',
                                lineHeight: '1.5',
                                margin: 0,
                                padding: 0,
                            }}
                        >
                            @{config.app.copyrightYear} {config.app.companyName} | {t('login.version')} {config.app.version}
                        </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default Login;
